// Substep 5-ο.3: Demand content preview.
//
// Two readers, one surface:
//
//   A paralegal about to generate a demand — see the chronology and
//   damages schedule BEFORE they land in a .docx, so a bad narrative
//   gets caught here rather than in a document an attorney is about to
//   sign.
//
//   A firm admin placing merge fields in their own demand template —
//   see exactly what each field produces, with the {{token}} to paste.
//   "Add the placeholders to your template" is otherwise an
//   instruction nobody can act on.
//
// Composition costs an LLM call for the narrative, so nothing loads
// until the user asks. No auto-fetch on mount.

import React, { useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

// Fields whose value is prose/tabular and wants a block presentation
// rather than an inline one.
const BLOCK_FIELDS = new Set([
  "medical_chronology", "damages_itemization", "diagnosis_summary",
]);

export function DemandContentCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showTokens, setShowTokens] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/matters/${matterId}/demand-preview`);
      setData(d);
      if (d.error) showNotification?.(d.error, "warning");
    } catch (e) {
      showNotification?.(`Preview failed: ${e.message}`, "error");
    }
    setLoading(false);
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      showNotification?.("Couldn't copy — select and copy manually.", "warning");
    }
  };

  const fields = data?.fields || [];
  const populated = fields.filter(f => !f.is_empty);

  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    }
  },
    // Header
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between",
               alignItems: "flex-start", gap: "12px", marginBottom: data ? "14px" : "0" }
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
                   letterSpacing: "0.08em", fontWeight: 600, margin: 0 }
        }, "Demand Content"),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted, marginTop: "4px", maxWidth: "620px" }
        }, "Medical chronology and damages schedule composed from verified records. Review before generating, and use the placeholder tokens to place these in the demand template."),
      ),
      React.createElement("button", {
        onClick: load, disabled: loading,
        style: {
          padding: "7px 15px", borderRadius: "8px", border: "none",
          background: loading ? C.warmGray : C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 600, cursor: loading ? "default" : "pointer",
          flexShrink: 0,
        }
      }, loading ? "Composing…" : (data ? "Recompose" : "Compose preview")),
    ),

    loading && !data && React.createElement("div", {
      style: { padding: "28px", textAlign: "center" }
    },
      React.createElement(Spinner, null),
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textMuted, marginTop: "10px" }
      }, "Writing the chronology from verified encounters…"),
    ),

    // Readiness + warnings
    data && data.readiness && !data.readiness.ready && React.createElement("div", {
      style: {
        padding: "10px 12px", borderRadius: "8px", marginBottom: "12px",
        background: `${C.error}0e`, border: `1px solid ${C.error}44`,
      }
    },
      React.createElement("div", {
        style: { fontSize: "12px", fontWeight: 700, color: C.textDark }
      }, "Composed over records that aren't demand-ready"),
      ...(data.readiness.blockers || []).map((b, i) => React.createElement("div", {
        key: i, style: { fontSize: "11px", color: C.error, marginTop: "3px" }
      }, "• " + b)),
      React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, marginTop: "5px" }
      }, "Usable as a working draft — verify the encounters before sending."),
    ),

    data && (data.warnings || []).length > 0 && React.createElement("div", {
      style: {
        padding: "10px 12px", borderRadius: "8px", marginBottom: "12px",
        background: C.warmWhite, border: `1px solid ${C.warmGray}`,
      }
    },
      React.createElement("div", {
        style: { fontSize: "9px", fontWeight: 700, color: C.textMuted,
                 letterSpacing: "0.06em", textTransform: "uppercase" }
      }, "Worth knowing"),
      ...(data.warnings || []).map((w, i) => React.createElement("div", {
        key: i, style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" }
      }, "• " + w)),
    ),

    // Composed content
    data && populated.length === 0 && !loading && React.createElement("div", {
      style: { padding: "22px", textAlign: "center", background: C.warmWhite,
               borderRadius: "8px", fontSize: "12px", color: C.textMuted }
    }, "Nothing to compose yet — no verified encounters on this matter. Process records on the Providers tab, then verify them."),

    data && populated.length > 0 && React.createElement("div", null,
      ...populated.map(f => React.createElement("div", {
        key: f.field,
        style: { marginBottom: "14px" }
      },
        React.createElement("div", {
          style: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }
        },
          React.createElement("span", {
            style: { fontSize: "12px", fontWeight: 700, color: C.navy }
          }, f.label),
          React.createElement("code", {
            style: {
              fontSize: "10px", padding: "1px 7px", borderRadius: "4px",
              background: `${C.gold}1c`, color: C.textDark, fontFamily: "monospace",
            }
          }, f.placeholder),
          React.createElement("button", {
            onClick: () => copy(f.placeholder, "tok-" + f.field),
            title: "Copy the placeholder to paste into the .docx template",
            style: {
              border: "none", background: "transparent", color: C.textMuted,
              fontSize: "10px", cursor: "pointer", padding: "1px 4px",
            }
          }, copied === "tok-" + f.field ? "copied" : "copy token"),
          React.createElement("div", { style: { flex: 1 } }),
          React.createElement("button", {
            onClick: () => copy(f.value, "val-" + f.field),
            title: "Copy the composed text",
            style: {
              border: `1px solid ${C.warmGray}`, background: "#fff",
              color: C.textMuted, fontSize: "10px", cursor: "pointer",
              padding: "2px 8px", borderRadius: "5px",
            }
          }, copied === "val-" + f.field ? "copied" : "copy text"),
        ),
        BLOCK_FIELDS.has(f.field)
          ? React.createElement("div", {
              style: {
                padding: "11px 13px", background: C.warmWhite,
                border: `1px solid ${C.warmGray}`, borderRadius: "8px",
                fontSize: "12px", color: C.textDark, lineHeight: 1.6,
                whiteSpace: "pre-wrap", wordWrap: "break-word",
                // The damages schedule is column-aligned with spaces;
                // a proportional font destroys it.
                fontFamily: f.field === "damages_itemization"
                  ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                  : "inherit",
                maxHeight: "340px", overflowY: "auto",
              }
            }, f.value)
          : React.createElement("div", {
              style: {
                padding: "7px 11px", background: C.warmWhite,
                border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                fontSize: "12px", color: C.textDark,
              }
            }, f.value),
      )),

      // Unpopulated fields, collapsed. Still worth listing: a firm
      // admin placing tokens needs to know a field exists even when
      // this particular matter has nothing for it.
      fields.length > populated.length && React.createElement("div",
        { style: { marginTop: "6px" } },
        React.createElement("button", {
          onClick: () => setShowTokens(v => !v),
          style: {
            background: "transparent", border: "none", cursor: "pointer",
            fontSize: "11px", color: C.textMuted, padding: "4px 0",
          }
        }, (showTokens ? "▾ " : "▸ ")
           + `${fields.length - populated.length} field(s) with no content on this matter`),
        showTokens && React.createElement("div", {
          style: { display: "flex", flexDirection: "column", gap: "3px", marginTop: "4px" }
        },
          ...fields.filter(f => f.is_empty).map(f => React.createElement("div", {
            key: f.field,
            style: { display: "flex", alignItems: "center", gap: "8px", fontSize: "11px" }
          },
            React.createElement("code", {
              style: { fontSize: "10px", padding: "1px 6px", borderRadius: "4px",
                       background: C.warmWhite, color: C.textMuted, fontFamily: "monospace" }
            }, f.placeholder),
            React.createElement("span", { style: { color: C.textMuted } }, f.label),
          )),
        ),
      ),

      React.createElement("div", {
        style: {
          marginTop: "14px", paddingTop: "11px",
          borderTop: `1px solid ${C.warmGray}`,
          fontSize: "11px", color: C.textMuted, lineHeight: 1.6,
        }
      },
        React.createElement("strong", { style: { color: C.textDark } },
          "To use these in the demand letter: "),
        "open the firm's demand .docx, paste the placeholder token where the content belongs, and re-upload it under Firm Settings → Templates. Generation fills them from verified records automatically.",
      ),
    ),
  );
}
